import { useEffect, useState } from 'react';
import { browserSubscriptionToRequest, fetchPushPublicKey, savePushSubscription } from '../lib/api/push';

export type PushNotificationState = 'unsupported' | 'install-required' | 'permission-default' | 'permission-denied' | 'subscribing' | 'subscribed' | 'error';

interface PushNotificationStatus {
  state: PushNotificationState;
  error: string;
  subscriptionId: string | null;
  enable(): Promise<void>;
}

export function usePushNotifications(): PushNotificationStatus {
  const [state, setState] = useState<PushNotificationState>(() => initialPushState());
  const [error, setError] = useState('');
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);

  useEffect(() => {
    if (isIOSBrowser() && !isStandalonePWA()) {
      setState('install-required');
      return;
    }
    if (!isPushSupported()) {
      setState('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setState('permission-denied');
      return;
    }

    let cancelled = false;
    void navigator.serviceWorker.ready.then(async (registration) => {
      const subscription = await registration.pushManager.getSubscription();
      if (cancelled) return;
      setState(subscription ? 'subscribed' : 'permission-default');
    }).catch((err) => {
      if (cancelled) return;
      setError(err instanceof Error ? err.message : 'Notification setup failed');
      setState('error');
    });

    return () => {
      cancelled = true;
    };
  }, []);

  async function enable() {
    if (isIOSBrowser() && !isStandalonePWA()) {
      setState('install-required');
      return;
    }
    if (!isPushSupported()) {
      setState('unsupported');
      return;
    }

    setError('');
    setState('subscribing');
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'denied') {
        setState('permission-denied');
        return;
      }
      if (permission !== 'granted') {
        setState('permission-default');
        return;
      }

      const [{ public_key: publicKey }, registration] = await Promise.all([
        fetchPushPublicKey(),
        navigator.serviceWorker.ready,
      ]);
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const saved = await savePushSubscription(browserSubscriptionToRequest(subscription));
      setSubscriptionId(saved.id);
      setState('subscribed');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Notification setup failed');
      setState('error');
    }
  }

  return { state, error, subscriptionId, enable };
}

function initialPushState(): PushNotificationState {
  if (isIOSBrowser() && !isStandalonePWA()) return 'install-required';
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'permission-denied';
  return 'permission-default';
}

function isPushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

function isIOSBrowser(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isStandalonePWA(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || ('standalone' in navigator && Boolean(navigator.standalone));
}

function urlBase64ToUint8Array(value: string): ArrayBuffer {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = `${value}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output.buffer;
}
